/* Grubwick admin — reusable menu-item list with inline CRUD.
 *
 * Renders the menu grouped into sections (Uber-Eats style): a sticky,
 * horizontally-scrollable bar of section pills sits above the list, jumps to a
 * section on click, and highlights the section you're scrolled into. Each
 * section shows its own table rows (edit/delete per row) plus the shared
 * add/edit modal and delete confirm. Controlled: it never owns the list — every
 * change is emitted through `onChange`. Shared by the restaurant editor and the
 * photo-import review panel so menu editing looks and behaves identically. */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { newId, type MenuItemRecord } from "../../../data/adminData";
import { PLACEHOLDER } from "../../../api/restaurants";
import { MenuItemModal, Confirm, SectionReorderModal, useToast } from "../ui/ui";
import { Plus, Pencil, Trash, Tags, List, Grip } from "../icons/icons";

type ItemModalState =
  | { item: MenuItemRecord }
  | { new: true; category?: string }
  | null;

const UNCATEGORIZED = "Other";

interface Section {
  id: string; // positional (sec-N) — used for scroll-spy + DOM refs
  label: string; // header / pill text
  category: string; // value to seed a new item with ("" for uncategorized)
  sectionId: string | null; // backend id (null for the uncategorized bucket)
  items: MenuItemRecord[];
}

// The API maps a missing section to the PLACEHOLDER string, so treat both that
// and an empty string as "no section".
function isUncategorized(category: string): boolean {
  return !category || category === PLACEHOLDER;
}

// Bucket items by category, preserving first-appearance order, with the single
// uncategorized bucket pushed last.
function groupIntoSections(items: MenuItemRecord[]): Section[] {
  const order: string[] = [];
  const byCat = new Map<string, MenuItemRecord[]>();
  const idByCat = new Map<string, string | null>();
  for (const m of items) {
    const key = isUncategorized(m.category) ? "" : m.category;
    if (!byCat.has(key)) {
      byCat.set(key, []);
      idByCat.set(key, m.sectionId); // backend id from the bucket's first item
      order.push(key);
    }
    byCat.get(key)!.push(m);
  }
  const sorted = [
    ...order.filter((k) => k !== ""),
    ...(order.includes("") ? [""] : []),
  ];
  return sorted.map((key, i) => ({
    id: `sec-${i}`,
    label: key === "" ? UNCATEGORIZED : key,
    category: key,
    sectionId: key === "" ? null : (idByCat.get(key) ?? null),
    items: byCat.get(key)!,
  }));
}

// Native `behavior: "smooth"` has a browser-fixed (fast) duration, so we tween
// scroll positions ourselves to control the pace. Kept gentle on purpose.
const SCROLL_MS = 850; // vertical page scroll to a section
const NAV_SCROLL_MS = 600; // horizontal section-bar scroll

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// Tween a number from→to over `duration` ms with eased motion, calling `apply`
// each frame. Returns a cancel function.
function tween(
  from: number,
  to: number,
  duration: number,
  apply: (v: number) => void,
): () => void {
  if (Math.abs(to - from) < 1) {
    apply(to);
    return () => {};
  }
  const start = performance.now();
  let raf = requestAnimationFrame(function step(now: number) {
    const t = Math.min(1, (now - start) / duration);
    apply(from + (to - from) * easeInOutCubic(t));
    if (t < 1) raf = requestAnimationFrame(step);
  });
  return () => cancelAnimationFrame(raf);
}

export function MenuItemsEditor({
  items,
  onChange,
  onReorderSections,
  emptyHint = "No menu items yet.",
  toolbarSlot,
  readOnly = false,
}: {
  items: MenuItemRecord[];
  onChange: (next: MenuItemRecord[]) => void;
  /* When provided (persisted location only), enables the "Reorder sections"
     button. Receives the new top-to-bottom list of backend section ids. */
  onReorderSections?: (orderedSectionIds: string[]) => void;
  emptyHint?: string;
  /* Optional DOM node (a slot in the parent's panel header) to render the
     Reorder/Add-item toolbar into. When given, the toolbar portals there
     instead of sitting above the list; the modals it opens stay owned here. */
  toolbarSlot?: HTMLElement | null;
  /* View-only: hide every mutation affordance (add / edit / delete / reorder).
     Used when the caller can view but not edit this menu (e.g. an owner looking
     at a restaurant they don't own). Section nav + scroll-spy stay live. */
  readOnly?: boolean;
}): React.ReactElement {
  const [modal, setModal] = useState<ItemModalState>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);
  const toast = useToast();

  const sections = useMemo(() => groupIntoSections(items), [items]);
  const showNav = sections.length > 1;

  // Sections with a backend id can be reordered; the uncategorized bucket can't
  // (it has no id and always sorts last). Need at least two to reorder.
  const reorderable = sections.filter((s) => s.category !== "" && s.sectionId);
  const otherSection = sections.find((s) => s.category === "");
  const canReorder = !!onReorderSections && reorderable.length > 1;

  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const navRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  // While a tab-click scroll is animating, ignore scroll-spy so the indicator
  // doesn't race through every section the page passes on the way.
  const suppressSpy = useRef(false);
  const spyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelScroll = useRef<(() => void) | null>(null);
  const cancelNav = useRef<(() => void) | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the section popover on outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return;
    function onDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  useEffect(
    () => () => {
      if (spyTimer.current) clearTimeout(spyTimer.current);
      cancelScroll.current?.();
      cancelNav.current?.();
    },
    [],
  );

  // Scroll-spy: highlight the pill for the section currently sitting under the
  // sticky bar. Each section becomes active when its header reaches a line
  // `offset` px below the top (its own `scroll-margin-top`, so click-jump and
  // spy agree). A short menu is the tricky case: its final sections can never
  // scroll up to that line (nothing below them to push them up), so a fixed
  // line could never select them. We compute each section's natural activation
  // scroll position and, for any tail that lands beyond the page's max scroll,
  // spread those evenly across the remaining scroll distance — so the tail
  // activates smoothly as you approach the bottom (last one exactly at bottom)
  // instead of an earlier section staying stuck or the highlight jumping.
  useEffect(() => {
    if (!showNav) return;
    let raf = 0;
    function computeActive() {
      raf = 0;
      if (suppressSpy.current) return;
      const y = window.scrollY;
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      // Can't scroll: the top section is the active one.
      if (maxScroll <= 0) {
        const first = sections[0];
        if (first) setActiveId(first.id);
        return;
      }
      const firstEl = sectionRefs.current.get(sections[0]?.id ?? "");
      if (!firstEl) return;
      const offset = parseFloat(getComputedStyle(firstEl).scrollMarginTop) || 0;
      // Natural activation scroll for each section: the scrollY at which its
      // header would reach the line. Monotonically increasing in DOM order.
      const nat = sections.map((s) => {
        const el = sectionRefs.current.get(s.id);
        if (!el) return Infinity;
        return Math.max(0, el.getBoundingClientRect().top + y - offset);
      });
      // Sections whose natural threshold exceeds the page's max scroll can never
      // reach the line — they form a suffix. Redistribute that suffix across the
      // leftover scroll (lastReachable → maxScroll] so each gets its own turn.
      const thresholds = nat.slice();
      const f = nat.findIndex((n) => n > maxScroll);
      if (f !== -1) {
        const base = f > 0 ? nat[f - 1] : 0;
        const k = sections.length - f;
        for (let i = f; i < sections.length; i++) {
          thresholds[i] = base + ((maxScroll - base) * (i - f + 1)) / k;
        }
      }
      let current = sections[0]?.id ?? null;
      for (let i = 0; i < sections.length; i++) {
        if (y + 1 >= thresholds[i]) current = sections[i].id;
        else break;
      }
      if (current) setActiveId(current);
    }
    function onScroll() {
      if (!raf) raf = requestAnimationFrame(computeActive);
    }
    computeActive();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [sections, showNav]);

  // activeId is positional (sec-N); fall back to the first section when it no
  // longer matches (e.g. a section emptied out).
  const activePill = sections.some((s) => s.id === activeId)
    ? activeId
    : sections[0]?.id;

  // Keep the active tab scrolled into view, pulling it toward the left so the
  // upcoming sections stay visible as you scroll down the menu (Uber-style).
  useEffect(() => {
    const nav = navRef.current;
    const tab = activePill ? tabRefs.current.get(activePill) : null;
    if (!nav || !tab) return;
    const target = Math.max(0, tab.offsetLeft - 16);
    cancelNav.current?.();
    cancelNav.current = tween(nav.scrollLeft, target, NAV_SCROLL_MS, (v) => {
      nav.scrollLeft = v;
    });
  }, [activePill]);

  function jumpTo(id: string) {
    const section = sectionRefs.current.get(id);
    setActiveId(id);
    if (!section) return;
    // Lock scroll-spy while the animated scroll runs, then resume — by then the
    // page has settled on the target section.
    suppressSpy.current = true;
    if (spyTimer.current) clearTimeout(spyTimer.current);
    spyTimer.current = setTimeout(() => {
      suppressSpy.current = false;
    }, SCROLL_MS + 150);

    const offset = parseFloat(getComputedStyle(section).scrollMarginTop) || 0;
    const targetTop =
      section.getBoundingClientRect().top + window.scrollY - offset;
    cancelScroll.current?.();
    cancelScroll.current = tween(window.scrollY, targetTop, SCROLL_MS, (v) =>
      window.scrollTo(0, v),
    );
  }

  function upsert(item: MenuItemRecord) {
    const editing = modal !== null && "item" in modal;
    const exists = items.some((m) => m.id === item.id);
    const next = exists
      ? items.map((m) => (m.id === item.id ? item : m))
      : [...items, { ...item, id: newId("m") }];
    onChange(next);
    setModal(null);
    toast(editing ? "Item updated" : "Item added");
  }

  function remove(id: string) {
    onChange(items.filter((m) => m.id !== id));
    setConfirmId(null);
    toast("Item removed");
  }

  const toolbar =
    items.length > 0 && !readOnly ? (
      <div className={`mi-toolbar${toolbarSlot ? " mi-toolbar--header" : ""}`}>
        {canReorder && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setReorderOpen(true)}
          >
            <Grip size={15} /> Reorder sections
          </button>
        )}
        <button
          className="btn btn-soft btn-sm"
          onClick={() => setModal({ new: true })}
        >
          <Plus size={15} /> Add item
        </button>
      </div>
    ) : null;

  return (
    <div className="mi-editor">
      {toolbarSlot
        ? toolbar && createPortal(toolbar, toolbarSlot)
        : toolbar}

      {showNav && (
        <div className="mi-secnav">
          <div className="mi-secnav-menu" ref={menuRef}>
            <button
              className={`mi-secnav-ic${menuOpen ? " is-open" : ""}`}
              aria-label="Jump to a section"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <List size={18} />
            </button>
            {menuOpen && (
              <div className="mi-secmenu" role="menu">
                <p className="mi-secmenu-h">Jump to section</p>
                {sections.map((s) => (
                  <button
                    key={s.id}
                    role="menuitem"
                    className={`mi-secmenu-item${s.id === activePill ? " is-active" : ""}`}
                    onClick={() => {
                      setMenuOpen(false);
                      jumpTo(s.id);
                    }}
                  >
                    <span className="mi-secmenu-label">{s.label}</span>
                    <span className="mi-secmenu-n">{s.items.length}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div
            className="mi-tabs"
            role="tablist"
            aria-label="Menu sections"
            ref={navRef}
          >
            {sections.map((s) => (
              <button
                key={s.id}
                role="tab"
                aria-selected={s.id === activePill}
                className={`mi-tab${s.id === activePill ? " is-active" : ""}`}
                ref={(el) => {
                  if (el) tabRefs.current.set(s.id, el);
                  else tabRefs.current.delete(s.id);
                }}
                onClick={() => jumpTo(s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {items.length ? (
        <div className="mi-sections">
          {sections.map((s) => (
            <div
              className="mi-section"
              key={s.id}
              ref={(el) => {
                if (el) sectionRefs.current.set(s.id, el);
                else sectionRefs.current.delete(s.id);
              }}
            >
              <div className="mi-section-head">
                <h4>
                  {s.label}
                  <span className="mi-section-n">{s.items.length}</span>
                </h4>
                {!readOnly && (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() =>
                      setModal({ new: true, category: s.category })
                    }
                  >
                    <Plus size={14} /> Add
                  </button>
                )}
              </div>
              <div className="mtable">
                <div className="mt-head">
                  <span>Item</span>
                  <span>Price</span>
                  <span></span>
                </div>
                {s.items.map((m) => (
                  <div className="mrow" key={m.id}>
                    <div className="m-cell-nm">
                      <div className="m-nm">{m.name}</div>
                      {m.desc && <div className="m-desc">{m.desc}</div>}
                    </div>
                    <div className="m-cell-price">
                      {m.price && m.price !== "—" ? (
                        <span className="m-price">{m.price}</span>
                      ) : (
                        <span className="m-price empty">—</span>
                      )}
                    </div>
                    <div className="mrow-actions">
                      {!readOnly && (
                        <>
                          <button
                            className="iconbtn"
                            onClick={() => setModal({ item: m })}
                            aria-label="Edit"
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            className="iconbtn del"
                            onClick={() => setConfirmId(m.id)}
                            aria-label="Delete"
                          >
                            <Trash size={15} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-menu">
          <div className="ic">
            <Tags size={34} />
          </div>
          <p>{emptyHint}</p>
          {!readOnly && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setModal({ new: true })}
            >
              <Plus size={15} /> Add manually
            </button>
          )}
        </div>
      )}

      {modal && (
        <MenuItemModal
          initial={
            "item" in modal
              ? modal.item
              : modal.category
                ? { id: "", name: "", price: "", category: modal.category, desc: "", sectionId: null }
                : undefined
          }
          onSave={upsert}
          onClose={() => setModal(null)}
        />
      )}
      {confirmId && (
        <Confirm
          title="Remove item?"
          danger
          confirmLabel="Remove"
          body="This menu item will be removed."
          onConfirm={() => remove(confirmId)}
          onClose={() => setConfirmId(null)}
        />
      )}
      {reorderOpen && canReorder && (
        <SectionReorderModal
          sections={reorderable.map((s) => ({
            id: s.sectionId!,
            label: s.label,
            count: s.items.length,
          }))}
          uncategorized={
            otherSection
              ? {
                  id: "__other__",
                  label: otherSection.label,
                  count: otherSection.items.length,
                }
              : null
          }
          onSave={(orderedIds) => {
            onReorderSections?.(orderedIds);
            setReorderOpen(false);
          }}
          onClose={() => setReorderOpen(false)}
        />
      )}
    </div>
  );
}
